import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, LogOut, Lock, Trash2, X, Check } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useProfile } from "../context/ProfileContext";
import { AVATARS } from "../data/curriculum";
import { AGE_BANDS, AGE_BAND_KEYS } from "../data/story";
import { toast } from "sonner";
import { formatApiErrorDetail } from "../services/api";
import { sfx } from "../services/soundfx";
import { speak } from "../services/phonicsAudio";

const COLORS = ["#F59E0B", "#FF6B6B", "#8B5CF6", "#22C55E", "#38BDF8", "#EC4899"];

export default function ProfilesPage() {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const { profiles, selectProfile, createProfile, deleteProfile, loading } = useProfile();
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState("");
  const [avatar, setAvatar] = useState("lion");
  const [color, setColor] = useState("#F59E0B");
  const [ageBand, setAgeBand] = useState("3-4");
  const [manage, setManage] = useState(false);

  const choose = (p) => {
    sfx.unlock();
    sfx.star();
    selectProfile(p);
    speak(`Hi ${p.name}! Let's read!`);
    navigate("/map");
  };

  const save = async () => {
    if (!name.trim()) return;
    try {
      sfx.correct();
      const p = await createProfile({ name: name.trim(), avatar, color, age_band: ageBand });
      setCreating(false);
      setName("");
      selectProfile(p);
      navigate("/map");
    } catch (e) {
      sfx.wrong();
      const msg = formatApiErrorDetail(e.response?.data?.detail) || "Could not add reader.";
      toast.error(msg);
      if (e.response?.status === 402) {
        setCreating(false);
        navigate("/parent");
      }
    }
  };

  return (
    <div className="min-h-screen p-5" style={{ background: "linear-gradient(180deg,#8B5CF6 0%,#C4B5FD 50%,#FBCFE8 100%)" }}>
      <div className="flex items-center justify-between max-w-4xl mx-auto mb-8">
        <h1 className="font-display text-3xl sm:text-4xl font-black text-white drop-shadow">Who's Reading?</h1>
        <div className="flex gap-2">
          <button
            data-testid="manage-profiles-btn"
            onClick={() => setManage((m) => !m)}
            className="pop-btn rounded-2xl bg-white/90 px-4 py-2 font-display font-bold text-slate-700 border-4 border-white shadow-[0_5px_0_#CBD5E1] active:shadow-none"
          >
            {manage ? "Done" : "Edit"}
          </button>
          <button
            data-testid="parent-portal-btn"
            onClick={() => { sfx.tap(); navigate("/parent"); }}
            className="pop-btn flex items-center gap-1 rounded-2xl bg-slate-800 px-4 py-2 font-display font-bold text-white border-4 border-white shadow-[0_5px_0_#1E293B] active:shadow-none"
          >
            <Lock className="w-4 h-4" /> Parents
          </button>
          <button
            data-testid="logout-btn"
            onClick={() => { sfx.tap(); logout(); }}
            className="pop-btn rounded-2xl bg-white/90 p-2 border-4 border-white shadow-[0_5px_0_#CBD5E1] active:shadow-none"
            aria-label="Log out"
          >
            <LogOut className="w-6 h-6 text-slate-600" />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-5 max-w-4xl mx-auto">
        {!loading &&
          profiles.map((p) => (
            <motion.div key={p.id} layout whileTap={{ scale: 0.95 }} className="relative">
              <button
                data-testid={`profile-card-${p.id}`}
                onClick={() => choose(p)}
                className="pop-btn w-full flex flex-col items-center rounded-[1.75rem] bg-white border-[5px] shadow-[0_8px_0_rgba(0,0,0,0.15)] active:shadow-none p-4"
                style={{ borderColor: p.color }}
              >
                <div className="flex items-center justify-center rounded-full mb-2" style={{ width: 84, height: 84, background: p.color + "22" }}>
                  <span className="text-5xl">{AVATARS[p.avatar]?.emoji || "🙂"}</span>
                </div>
                <span className="font-display text-lg font-bold text-slate-800 truncate w-full text-center">{p.name}</span>
                <span className="text-sm font-bold text-amber-500">⭐ {p.total_stars}</span>
              </button>
              {manage && (
                <button
                  data-testid={`delete-profile-${p.id}`}
                  onClick={() => deleteProfile(p.id)}
                  className="absolute -top-2 -right-2 rounded-full bg-red-500 p-2 border-2 border-white shadow"
                >
                  <Trash2 className="w-4 h-4 text-white" />
                </button>
              )}
            </motion.div>
          ))}

        <motion.button
          layout
          data-testid="add-profile-btn"
          onClick={() => { sfx.tap(); setCreating(true); }}
          className="pop-btn flex flex-col items-center justify-center rounded-[1.75rem] bg-white/40 border-[5px] border-dashed border-white min-h-[160px] text-white"
        >
          <Plus className="w-12 h-12" strokeWidth={3} />
          <span className="font-display font-bold">Add Kid</span>
        </motion.button>
      </div>

      {/* Create modal */}
      <AnimatePresence>
        {creating && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "#0F172A99", backdropFilter: "blur(6px)" }}>
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              className="w-full max-w-md rounded-[2rem] bg-white border-[6px] border-violet-300 shadow-2xl p-6"
            >
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-display text-2xl font-black text-violet-700">New Reader</h2>
                <button data-testid="close-create-btn" onClick={() => setCreating(false)} className="rounded-full bg-slate-100 p-2">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <label className="font-display font-bold text-slate-500 mb-2 block">What's your reader's name?</label>
              <input
                data-testid="profile-name-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Kid's name"
                className="w-full rounded-2xl border-4 border-slate-200 focus:border-violet-300 outline-none px-4 py-3 font-bold text-slate-700 mb-2"
              />
              <p className="text-xs font-semibold text-slate-400 mb-4 px-1 leading-snug">
                We use their first name to cheer them on and to help them recognize and spell their own name during activities. 💜
              </p>

              <p className="font-display font-bold text-slate-500 mb-2">Pick a buddy</p>
              <div className="grid grid-cols-4 gap-2 mb-4">
                {Object.entries(AVATARS).map(([key, a]) => (
                  <button
                    key={key}
                    data-testid={`avatar-${key}`}
                    onClick={() => { sfx.tap(); setAvatar(key); }}
                    className={`pop-btn rounded-2xl p-2 text-4xl border-4 ${avatar === key ? "border-violet-400 bg-violet-50" : "border-transparent bg-slate-50"}`}
                  >
                    {a.emoji}
                  </button>
                ))}
              </div>

              <p className="font-display font-bold text-slate-500 mb-2">Pick a color</p>
              <div className="flex gap-3 mb-6">
                {COLORS.map((c) => (
                  <button
                    key={c}
                    data-testid={`color-${c}`}
                    onClick={() => setColor(c)}
                    className="rounded-full border-4 flex items-center justify-center"
                    style={{ width: 44, height: 44, background: c, borderColor: color === c ? "#1E293B" : "#fff" }}
                  >
                    {color === c && <Check className="w-5 h-5 text-white" strokeWidth={3} />}
                  </button>
                ))}
              </div>

              <p className="font-display font-bold text-slate-500 mb-2">How old is your reader?</p>
              <div className="grid grid-cols-3 gap-2 mb-6">
                {AGE_BAND_KEYS.map((k) => (
                  <button
                    key={k}
                    data-testid={`age-band-${k}`}
                    onClick={() => { sfx.tap(); setAgeBand(k); }}
                    className={`rounded-2xl p-2 border-4 text-center ${ageBand === k ? "border-violet-400 bg-violet-50" : "border-slate-200 bg-slate-50"}`}
                  >
                    <div className="font-display font-black text-slate-800 text-sm">{AGE_BANDS[k].label}</div>
                    <div className="text-[10px] font-bold text-slate-400">{AGE_BANDS[k].focus}</div>
                  </button>
                ))}
              </div>

              <button
                data-testid="save-profile-btn"
                onClick={save}
                className="pop-btn w-full rounded-2xl bg-green-500 text-white font-display text-xl font-bold py-4 border-4 border-white shadow-[0_6px_0_#15803D] active:shadow-none"
              >
                Start Reading!
              </button>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}
